import './Hero.scss'

import {ImageType, LinkType, Mod, WysiwygType} from '../../types'
import React, {useState} from 'react'

import {Background} from '@codeurs/front'
import {Icon} from '../ui/Icon'
import {Link} from '../../util/link'
import {MarkedTitle} from '../ui/MarkedTitle'
import {Theme} from '../Theme'
import {style} from '../../util/style'
import {useLanguage} from '../../util/i18n'
import {useLocation} from 'react-router'

const styles = style('hero', ['image', 'content', 'side', 'cta'])

export type HeroType = HeroImageType & HeroContentType

type HeroImageType = {
	image?: ImageType
}

type HeroContentType = {
	markedtitle?: string
	title: string
	subtitle?: string
	text?: WysiwygType
	button?: LinkType & {full_path: string}
}

export const Hero: React.FC<HeroType & {mod?: Mod<'home'>}> = ({
	image,
	markedtitle,
	title,
	subtitle,
	text,
	button,
	mod
}) => {
	const projectKey = window['__INITIAL_STATE__'].project_key
	const isSubsite = projectKey !== 'broederlijkdelen'

	return (
		<div className={styles().mod(mod).mod({subsite: isSubsite}).mod({image})}>
			{isSubsite && (
				<HeroCta
					url="https://www.broederlijkdelen.be"
					title="Broederlijk Delen"
				/>
			)}
			<HeroImage image={image} />
			<Theme.Container>
				<HeroContent
					markedtitle={markedtitle}
					title={title}
					subtitle={subtitle}
					text={text}
					button={button}
				/>
			</Theme.Container>
		</div>
	)
}

const HeroImage: React.FC<HeroImageType> = ({image}) => {
	if (!image) return null
	const [isLoaded, setLoaded] = useState(false)

	return (
		<div className={styles.image().is({loaded: isLoaded})}>
			<Background
				img={image}
				className={styles.image('bg').toString()}
				onload={() => setLoaded(true)}
			/>
		</div>
	)
}

const HeroContent: React.FC<HeroContentType> = ({
	markedtitle,
	title,
	subtitle,
	text,
	button
}) => {
	const lang = useLanguage()
	const {pathname} = useLocation()

	const urlLinksToCurrentpage =
		button?.url === pathname || `/${lang}${button?.full_path}` === pathname

	return (
		<div className={styles.content()} data-gt-block>
			{markedtitle && (
				<Theme.H3
					className={styles.content('markedtitle')}
					mod={['white', 'marked', 'slantleft']}
				>
					{markedtitle}
				</Theme.H3>
			)}
			<Theme.H1 className={styles.content('title')} mod="green">
				{title}
			</Theme.H1>
			{subtitle && (
				<Theme.H5 as="h2" className={styles.content('subtitle')}>
					{subtitle}
				</Theme.H5>
			)}
			{subtitle && text && <br />}
			{text && (
				<div className={styles.content('text')}>
					<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
				</div>
			)}
			{!urlLinksToCurrentpage && button?.url && (
				<div className={styles.content('button')}>
					<Theme.Button to={button.url}>
						{button.description || button.title}
					</Theme.Button>
				</div>
			)}
		</div>
	)
}

export const HeroSide: React.FC<HeroType> = ({
	title,
	subtitle,
	text,
	button
}) => {
	const lang = useLanguage()
	const {pathname} = useLocation()

	const urlLinksToCurrentpage =
		button?.url === pathname || `/${lang}${button?.full_path}` === pathname

	return (
		<div className={styles.side()}>
			<Theme.H1 className={styles.side('title')} mod="green">
				{title}
			</Theme.H1>
			{subtitle && (
				<Theme.H4 as="h2" className={styles.side('subtitle')}>
					{subtitle}
				</Theme.H4>
			)}
			{text && (
				<div className={styles.side('text')}>
					<Theme.Wysiwyg>{text}</Theme.Wysiwyg>
				</div>
			)}
			{!urlLinksToCurrentpage && button?.url && (
				<div className={styles.side('button')}>
					<Theme.Button to={button.url}>
						{button.description || button.title}
					</Theme.Button>
				</div>
			)}
		</div>
	)
}

const HeroCta: React.FC<{url: string; title?: string}> = ({url, title}) =>
	url && (
		<Link to={url} className={styles.cta()}>
			<span className={styles.cta('icon')}>
				<Icon icon={'compass'} />
			</span>
			<span className={styles.cta('title')}>{title || url}</span>
		</Link>
	)
